/* @jsxImportSource @emotion/react */
/* eslint-disable formatjs/no-literal-string-in-jsx */
import React, { useState } from 'react'
import { css } from '@emotion/react'
import classNames from 'classnames'
import { AnimatePresence, motion } from 'motion/react'
import { FormattedMessage } from 'react-intl'

import {
  CustomNoteIcon,
  OrganizationBadge,
  SidebarRowLayout,
  SidebarSection,
  SidebarShell,
} from '@slite/components'
import {
  CaretNavigationRightIcon,
  SearchIcon,
  SliteAgentIcon,
  SpellcheckIcon,
} from '@slite/icons'

import type { DocNode } from './content'

const SIDEBAR_ROW_INDENTATION = 20

const sidebarCss = css`
  .sidebar-row--selected {
    background-color: var(--alpha-shade-50);
  }

  .sidebar-row--selected .sidebar-row-text {
    color: var(--sidebar-item-hover-text-color, var(--shade-05));
  }
`

type TreeNodeProps = {
  node: DocNode
  depth: number
  currentId: string
  expanded: Set<string>
}

const TreeNode = ({ node, depth, currentId, expanded }: TreeNodeProps) => {
  const isFolder = !!node.children
  const isExpanded = expanded.has(node.id)
  const isActive = node.id === currentId
  const rowOffset = SIDEBAR_ROW_INDENTATION * depth

  return (
    <>
      <SidebarRowLayout
        className="width-full"
        depth={depth}
        isActive={isActive}
        style={{
          minWidth: 0,
          width: rowOffset > 0 ? `calc(100% - ${rowOffset}px)` : '100%',
        }}
        icon={
          <CustomNoteIcon
            size={16}
            iconShape={node.icon?.shape ?? null}
            iconColor={node.icon?.color ?? null}
            expanded={isExpanded}
          />
        }
        leftPart={
          isFolder ? (
            <CaretNavigationRightIcon
              size={14}
              className={classNames('shade-30', { 'rotate-90': isExpanded })}
              style={{ transition: 'transform 0.13s ease-in-out' }}
            />
          ) : (
            <span />
          )
        }
        label={node.title}
      />
      <AnimatePresence initial={false}>
        {isExpanded && node.children && (
          <motion.div
            key="children"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{
              height: { duration: 0.28, ease: [0.32, 0.72, 0, 1] },
              opacity: { duration: 0.2, ease: 'easeOut' },
            }}
            style={{ overflow: 'hidden' }}
          >
            {node.children.map((child) => (
              <TreeNode
                key={child.id}
                node={child}
                depth={depth + 1}
                currentId={currentId}
                expanded={expanded}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

type SidebarProps = {
  tree: DocNode[]
  currentId: string
  expanded: Set<string>
}

export const Sidebar = ({ tree, currentId, expanded }: SidebarProps) => {
  const [channelsExpanded, setChannelsExpanded] = useState(true)
  return (
    <SidebarShell
      css={sidebarCss}
      className="display-none-small display-none-medium"
      style={{ width: 200, flexShrink: 0, marginLeft: -4 }}
    >
      <div
        style={{
          minHeight: 48,
          display: 'flex',
          alignItems: 'center',
          padding: '0 20px',
        }}
      >
        <OrganizationBadge name="The Nice Co." isButton={false} />
      </div>
      <SidebarSection className="padding-bottom-12">
        <SidebarRowLayout
          className="width-full"
          icon={<SliteAgentIcon size={16} />}
          label={
            <FormattedMessage
              id="new.hero.demo.sidebar.superAgent"
              defaultMessage="Agent"
            />
          }
        />
        <SidebarRowLayout
          className="width-full"
          icon={<SearchIcon size={16} />}
          label={
            <FormattedMessage
              id="new.hero.demo.sidebar.search"
              defaultMessage="Search"
            />
          }
        />
        <SidebarRowLayout
          className="width-full"
          icon={<SpellcheckIcon size={16} />}
          label={
            <FormattedMessage
              id="new.hero.demo.sidebar.agentTriage"
              defaultMessage="Agent triage"
            />
          }
        />
      </SidebarSection>
      <SidebarSection
        title={
          <FormattedMessage
            id="new.hero.demo.sidebar.channels"
            defaultMessage="Channels"
          />
        }
        isExpanded={channelsExpanded}
        onToggleExpand={() => setChannelsExpanded((v) => !v)}
      >
        {tree.map((node) => (
          <TreeNode
            key={node.id}
            node={node}
            depth={0}
            currentId={currentId}
            expanded={expanded}
          />
        ))}
      </SidebarSection>
    </SidebarShell>
  )
}
